import type {
  HistoricalFigure,
  Question,
  ScoreVector,
} from "./types.ts";

export const TEST_VERSION = 1;
export const SCORING_VERSION = "centered-cosine-v1";
export const TEST_SLUG = "ming-jing";

export const dimensionLabels: Record<keyof ScoreVector, string> = {
  decisiveness: "决断力",
  strategy: "谋略性",
  idealism: "理想主义",
  influence: "社交影响",
  risk: "风险偏好",
  order: "秩序倾向",
};

const v = (
  decisiveness: number,
  strategy: number,
  idealism: number,
  influence: number,
  risk: number,
  order: number,
): ScoreVector => ({
  decisiveness,
  strategy,
  idealism,
  influence,
  risk,
  order,
});

export const questions: Question[] = [
  {
    id: "q01",
    eyebrow: "局势变化",
    text: "一个投入已久的计划突然失去原有条件，你通常会怎么做？",
    options: [
      {
        id: "a",
        text: "先定一个新方向，让大家在行动中修正",
        scores: v(2, 0, 0, 1, 1, -1),
      },
      {
        id: "b",
        text: "重新梳理变量，准备几条可能的后路",
        scores: v(0, 2, 0, 0, -1, 1),
      },
      {
        id: "c",
        text: "守住最初想解决的问题，换一种实现方式",
        scores: v(0, 0, 2, 0, 0, 0),
      },
      {
        id: "d",
        text: "先听各方判断，再把人和资源重新组合",
        scores: v(-1, 1, 0, 2, 0, 0),
      },
    ],
  },
  {
    id: "q02",
    eyebrow: "未知机会",
    text: "一个回报诱人但信息并不完整的机会来到面前，你会？",
    options: [
      {
        id: "a",
        text: "抓住窗口先进去，重要信息边做边补",
        scores: v(2, 0, 0, 0, 2, -1),
      },
      {
        id: "b",
        text: "做最坏情形推演，只下注可承受的部分",
        scores: v(0, 2, 0, 0, 0, 1),
      },
      {
        id: "c",
        text: "看它是否符合长期方向，不被短期回报带走",
        scores: v(-1, 0, 2, 0, -1, 0),
      },
      {
        id: "d",
        text: "找到可信的人一起判断，借助彼此的信息差",
        scores: v(0, 1, 0, 2, 0, 0),
      },
    ],
  },
  {
    id: "q03",
    eyebrow: "意见相左",
    text: "你确信一件事是对的，但身边多数人都不赞同，此时你更可能？",
    options: [
      {
        id: "a",
        text: "直接推进一个小结果，用事实结束争论",
        scores: v(2, 0, 1, 0, 1, -1),
      },
      {
        id: "b",
        text: "暂不表态，先弄清每个人真正担心什么",
        scores: v(-1, 2, 0, 1, -1, 0),
      },
      {
        id: "c",
        text: "即使暂时孤立，也不轻易放弃自己的原则",
        scores: v(0, -1, 2, -1, 0, 0),
      },
      {
        id: "d",
        text: "换一种大家能接受的说法，慢慢争取支持",
        scores: v(0, 1, 0, 2, 0, 1),
      },
    ],
  },
  {
    id: "q04",
    eyebrow: "漫长回报",
    text: "一件重要的事很久都看不到回报，你靠什么继续？",
    options: [
      {
        id: "a",
        text: "给自己设下一段明确期限，到点就做取舍",
        scores: v(2, 0, 0, 0, 0, 1),
      },
      {
        id: "b",
        text: "拆成可以验证的小步骤，持续调整路线",
        scores: v(0, 2, 0, 0, 0, 2),
      },
      {
        id: "c",
        text: "只要仍相信它值得，慢一点也可以接受",
        scores: v(-1, 0, 2, 0, -1, 0),
      },
      {
        id: "d",
        text: "找同路人彼此鼓劲，让过程不再孤单",
        scores: v(0, 0, 1, 2, 0, -1),
      },
    ],
  },
  {
    id: "q05",
    eyebrow: "站到台前",
    text: "临时需要有人代表团队面对一群陌生人，你会？",
    options: [
      {
        id: "a",
        text: "接过话筒，先把现场气氛和方向稳住",
        scores: v(2, 0, 0, 2, 1, 0),
      },
      {
        id: "b",
        text: "先快速准备结构，确保关键信息不遗漏",
        scores: v(0, 2, 0, 0, -1, 2),
      },
      {
        id: "c",
        text: "如果有更合适的人，愿意在幕后支持",
        scores: v(-1, 1, 1, -1, -1, 1),
      },
      {
        id: "d",
        text: "从听众关心的事说起，边交流边建立信任",
        scores: v(0, 0, 0, 2, 0, -1),
      },
    ],
  },
  {
    id: "q06",
    eyebrow: "规则与结果",
    text: "现有规则明显拖慢了一件急事，你更倾向于？",
    options: [
      {
        id: "a",
        text: "先把问题解决，再为自己的决定承担后果",
        scores: v(2, 0, 0, 0, 2, -2),
      },
      {
        id: "b",
        text: "找到规则留下的空间，在边界内绕开阻碍",
        scores: v(0, 2, 0, 0, 0, 1),
      },
      {
        id: "c",
        text: "推动改变规则，让后来的人不再遇到同样问题",
        scores: v(1, 1, 2, 1, 1, 0),
      },
      {
        id: "d",
        text: "先按程序处理，稳定比一时效率更重要",
        scores: v(-1, -1, 0, 0, -2, 2),
      },
    ],
  },
  {
    id: "q07",
    eyebrow: "复杂难题",
    text: "面对一个线索很多、暂时没有答案的难题，你通常从哪里开始？",
    options: [
      {
        id: "a",
        text: "找最关键的一环，先做一次有信息量的尝试",
        scores: v(2, 1, 0, 0, 1, -1),
      },
      {
        id: "b",
        text: "画出全局关系，寻找被忽略的因果和约束",
        scores: v(0, 2, 0, -1, 0, 1),
      },
      {
        id: "c",
        text: "回到最初目的，确认问题本身有没有问错",
        scores: v(-1, 1, 2, 0, 0, 0),
      },
      {
        id: "d",
        text: "召集不同经验的人，让答案在碰撞中出现",
        scores: v(0, 1, 0, 2, 0, 0),
      },
    ],
  },
  {
    id: "q08",
    eyebrow: "自由的一天",
    text: "如果突然多出一整天空闲时间，什么最容易吸引你？",
    options: [
      {
        id: "a",
        text: "去一个没去过的地方，让意外决定行程",
        scores: v(0, -1, 1, 0, 2, -2),
      },
      {
        id: "b",
        text: "完成搁置已久的计划，享受清空清单的感觉",
        scores: v(1, 1, 0, -1, -1, 2),
      },
      {
        id: "c",
        text: "独处、阅读或创作，把注意力还给自己",
        scores: v(-1, 0, 2, -2, -1, 0),
      },
      {
        id: "d",
        text: "约上喜欢的人，去制造一段共同记忆",
        scores: v(0, 0, 1, 2, 1, -1),
      },
    ],
  },
  {
    id: "q09",
    eyebrow: "突发失误",
    text: "重要场合出现了一个可能影响全局的失误，你第一反应是？",
    options: [
      {
        id: "a",
        text: "立刻止损，先让局面重新回到可控状态",
        scores: v(2, 0, 0, 0, 1, 1),
      },
      {
        id: "b",
        text: "判断它会引发哪些连锁反应，再选择动作",
        scores: v(0, 2, 0, 0, -1, 1),
      },
      {
        id: "c",
        text: "主动说明问题，不让别人替自己承担代价",
        scores: v(1, 0, 2, 0, 0, 1),
      },
      {
        id: "d",
        text: "稳住相关的人，避免恐慌把问题继续放大",
        scores: v(0, 1, 0, 2, 0, 0),
      },
    ],
  },
  {
    id: "q10",
    eyebrow: "功劳归属",
    text: "你付出很多的成果被别人拿走了大部分注意力，你会？",
    options: [
      {
        id: "a",
        text: "当场把事实说清楚，不让边界继续模糊",
        scores: v(2, 0, 1, 0, 1, 0),
      },
      {
        id: "b",
        text: "先记下这次信号，调整以后合作和资源安排",
        scores: v(0, 2, -1, -1, 0, 1),
      },
      {
        id: "c",
        text: "如果事情本身有价值，不急着争一时评价",
        scores: v(-1, 0, 2, -1, -1, 0),
      },
      {
        id: "d",
        text: "私下沟通，让对方有机会把关系修复回来",
        scores: v(0, 1, 0, 2, -1, 1),
      },
    ],
  },
  {
    id: "q11",
    eyebrow: "陌生旅程",
    text: "要去一个完全陌生的地方，你最自然的准备方式是？",
    options: [
      {
        id: "a",
        text: "定好出发时间，其他事情到了再说",
        scores: v(1, -1, 0, 0, 2, -2),
      },
      {
        id: "b",
        text: "研究路线和备选方案，提前避开明显风险",
        scores: v(0, 2, 0, -1, -1, 2),
      },
      {
        id: "c",
        text: "先了解那里的故事，想清楚自己为何而去",
        scores: v(-1, 0, 2, 0, 0, 0),
      },
      {
        id: "d",
        text: "联系当地的人，比攻略更相信真实交流",
        scores: v(0, 0, 0, 2, 1, -1),
      },
    ],
  },
  {
    id: "q12",
    eyebrow: "写给年轻的自己",
    text: "如果只能送给年轻时的自己一句提醒，你会选？",
    options: [
      {
        id: "a",
        text: "别等所有条件齐全，先迈出决定性的一步",
        scores: v(2, 0, 0, 0, 2, -1),
      },
      {
        id: "b",
        text: "不要只看眼前输赢，要看局势怎样慢慢形成",
        scores: v(0, 2, 0, 0, -1, 1),
      },
      {
        id: "c",
        text: "知道自己不愿交换什么，比得到什么更重要",
        scores: v(-1, 0, 2, 0, -1, 0),
      },
      {
        id: "d",
        text: "很多难题不是靠更强，而是靠理解更多的人",
        scores: v(0, 1, 1, 2, 0, 0),
      },
    ],
  },
  {
    id: "q13",
    eyebrow: "带领众人",
    text: "需要带一群性格不同的人完成艰难任务，你更依赖什么？",
    options: [
      {
        id: "a",
        text: "清楚的决定和节奏，让所有人知道下一步",
        scores: v(2, 0, 0, 1, 0, 2),
      },
      {
        id: "b",
        text: "让每个人待在最合适的位置，彼此形成配合",
        scores: v(0, 2, 0, 1, 0, 1),
      },
      {
        id: "c",
        text: "共同相信的目标，困难时才不容易散掉",
        scores: v(0, 0, 2, 1, 0, 0),
      },
      {
        id: "d",
        text: "对人的感受和欲望足够敏锐，知道如何鼓动",
        scores: v(0, 1, -1, 2, 1, -1),
      },
    ],
  },
  {
    id: "q14",
    eyebrow: "留下些什么",
    text: "回望很长的一生，你最希望自己留下什么？",
    options: [
      {
        id: "a",
        text: "几个真正改变局面的关键决定",
        scores: v(2, 1, 0, 0, 1, 0),
      },
      {
        id: "b",
        text: "一套后来的人仍能使用的方法和秩序",
        scores: v(0, 2, 0, 0, -1, 2),
      },
      {
        id: "c",
        text: "一种没有因现实妥协掉的精神或作品",
        scores: v(-1, 0, 2, -1, 0, -1),
      },
      {
        id: "d",
        text: "一群被自己连接、影响或成就过的人",
        scores: v(0, 0, 1, 2, 0, 0),
      },
    ],
  },
];

export const figures: HistoricalFigure[] = [
  {
    slug: "zhuge-liang",
    name: "诸葛亮",
    era: "三国",
    role: "谋略者",
    monogram: "亮",
    color: "#426a5a",
    labels: ["深谋远虑", "责任感", "秩序建构"],
    tagline: "你相信真正的从容，来自提前看见风向。",
    summary:
      "你习惯先看全局，再把复杂问题拆成可以推进的步骤。比起抢先表达，你更在意承诺能否兑现、系统能否长久运转。",
    profile: v(0.25, 0.95, 0.55, 0.05, -0.55, 0.9),
    keyDimensions: ["strategy", "order"],
    priority: 10,
  },
  {
    slug: "li-bai",
    name: "李白",
    era: "盛唐",
    role: "自由表达者",
    monogram: "白",
    color: "#507aa6",
    labels: ["自由想象", "强烈表达", "不受拘束"],
    tagline: "你更愿意忠于生命的亮度，而不是安全的刻度。",
    summary:
      "你对新鲜、真诚与精神自由有天然敏感。你需要空间表达自己，也愿意为一次真正打动你的体验离开熟悉轨道。",
    profile: v(0.2, -0.55, 0.9, 0.55, 0.9, -0.9),
    keyDimensions: ["idealism", "risk"],
    priority: 20,
  },
  {
    slug: "wang-yangming",
    name: "王阳明",
    era: "明代",
    role: "知行实践者",
    monogram: "明",
    color: "#9a5f48",
    labels: ["内在坚定", "知行合一", "独立判断"],
    tagline: "你不等世界给出答案，行动本身就是求证。",
    summary:
      "你重视内在判断，也不满足于只在头脑里正确。越到复杂时刻，你越希望通过实际行动确认方向。",
    profile: v(0.75, 0.35, 0.85, 0.05, 0.2, 0.2),
    keyDimensions: ["decisiveness", "idealism"],
    priority: 30,
  },
  {
    slug: "su-shi",
    name: "苏轼",
    era: "北宋",
    role: "豁达创造者",
    monogram: "轼",
    color: "#b8754f",
    labels: ["豁达适应", "创造表达", "生活感受"],
    tagline: "你能把命运给的转弯，过成另一种风景。",
    summary:
      "你既有原则，也有很强的生活恢复力。遭遇变化时，你往往不是假装无事，而是重新找到值得热爱的具体事物。",
    profile: v(0.05, 0.0, 0.7, 0.55, 0.25, -0.45),
    keyDimensions: ["idealism", "influence"],
    priority: 40,
  },
  {
    slug: "wu-zetian",
    name: "武则天",
    era: "唐代",
    role: "权势开拓者",
    monogram: "曌",
    color: "#854c65",
    labels: ["强势决断", "识人用人", "突破边界"],
    tagline: "你不等待位置被让出来，而会证明自己配得上它。",
    summary:
      "你对权力结构和现实机会十分敏锐。面对阻力时，你更可能提高掌控力，而不是降低目标。",
    profile: v(0.95, 0.75, -0.15, 0.85, 0.65, 0.55),
    keyDimensions: ["decisiveness", "influence"],
    priority: 50,
  },
  {
    slug: "zhang-juzheng",
    name: "张居正",
    era: "明代",
    role: "务实改革者",
    monogram: "居",
    color: "#6d604a",
    labels: ["务实改革", "效率纪律", "结果负责"],
    tagline: "你关心的不是方案听起来多好，而是它能否真的运转。",
    summary:
      "你倾向把宏大问题落到制度、节奏和责任上。比起温和地维持现状，你愿意为了有效结果推动艰难改变。",
    profile: v(0.7, 0.75, -0.2, 0.0, -0.05, 0.95),
    keyDimensions: ["order", "strategy"],
    priority: 60,
  },
  {
    slug: "liu-bang",
    name: "刘邦",
    era: "汉初",
    role: "人心整合者",
    monogram: "邦",
    color: "#8a6f3b",
    labels: ["灵活应变", "知人善任", "机会嗅觉"],
    tagline: "你未必事事最强，却知道怎样让强者愿意同行。",
    summary:
      "你对人和局势的变化很敏锐，能承认自己不会什么，也能迅速抓住转机。你更相信有效组合，而不是独自证明。",
    profile: v(0.55, 0.35, -0.45, 0.95, 0.75, -0.55),
    keyDimensions: ["influence", "risk"],
    priority: 70,
  },
  {
    slug: "tao-yuanming",
    name: "陶渊明",
    era: "东晋",
    role: "内在自洽者",
    monogram: "陶",
    color: "#748458",
    labels: ["内在自由", "淡泊清醒", "自我节奏"],
    tagline: "你不急着赢过世界，更在意没有输掉自己。",
    summary:
      "你对外部评价和权力竞争保持距离，更愿意维护生活的真实感。你需要独处，也擅长从简单事物里恢复能量。",
    profile: v(-0.55, -0.2, 0.85, -0.9, -0.65, -0.35),
    keyDimensions: ["idealism", "influence"],
    priority: 80,
  },
  {
    slug: "li-qingzhao",
    name: "李清照",
    era: "两宋之际",
    role: "敏锐记录者",
    monogram: "清",
    color: "#7e688c",
    labels: ["细腻敏锐", "真实表达", "韧性记忆"],
    tagline: "你能察觉细微变化，也不愿把真实感受写得含糊。",
    summary:
      "你对语言、关系与环境的细节非常敏感。柔软并不等于脆弱，经历变化后，你更会把复杂感受沉淀成清醒表达。",
    profile: v(-0.15, 0.15, 0.8, 0.2, -0.05, -0.25),
    keyDimensions: ["idealism", "strategy"],
    priority: 90,
  },
  {
    slug: "cao-cao",
    name: "曹操",
    era: "东汉末",
    role: "乱局行动者",
    monogram: "操",
    color: "#5d5a67",
    labels: ["现实洞察", "快速决断", "不惧争议"],
    tagline: "你宁可在不完整的信息里行动，也不愿把机会让给犹豫。",
    summary:
      "你对局势、人才和风险有强烈嗅觉。相比维护一个纯粹形象，你更愿意承担争议，把现实问题推向结果。",
    profile: v(0.95, 0.85, -0.65, 0.55, 0.8, 0.05),
    keyDimensions: ["decisiveness", "strategy"],
    priority: 100,
  },
  {
    slug: "zheng-he",
    name: "郑和",
    era: "明代",
    role: "远航组织者",
    monogram: "和",
    color: "#347b82",
    labels: ["探索勇气", "组织纪律", "跨界沟通"],
    tagline: "你敢去远方，也知道真正的远航离不开秩序。",
    summary:
      "你身上同时存在探索欲和组织感。你不是只凭冲动冒险，而是愿意为未知准备、协调并承担长期责任。",
    profile: v(0.5, 0.45, 0.05, 0.6, 0.85, 0.7),
    keyDimensions: ["risk", "order"],
    priority: 110,
  },
  {
    slug: "ban-zhao",
    name: "班昭",
    era: "东汉",
    role: "学识传承者",
    monogram: "昭",
    color: "#8f705c",
    labels: ["沉静学识", "秩序意识", "长期传承"],
    tagline: "你相信安静而准确的工作，也能穿过很长的时间。",
    summary:
      "你不一定追逐最显眼的位置，却重视知识、责任和完成度。你愿意接续未完成的工作，让经验真正被后来者使用。",
    profile: v(-0.25, 0.55, 0.5, -0.35, -0.75, 0.9),
    keyDimensions: ["order", "idealism"],
    priority: 120,
  },
];

export const figureBySlug = new Map(
  figures.map((figure) => [figure.slug, figure]),
);
